/**
 * assets/component-product-recommendations.js
 *
 * Two custom elements for secondary product discovery on product pages:
 *
 *   <recently-viewed-products>
 *     Tracks the current product handle in localStorage on every visit.
 *     Lazy-loads up to 4 previously viewed products via /products/{handle}.json,
 *     deduplicating against every other recommendation carousel on the page.
 *
 *   <customers-also-bought>
 *     Fetches Shopify ML recommendations via the section rendering API
 *     (sections/customers-also-bought.liquid), deduplicating against all other
 *     carousels. Limits display to 4 cards.
 *
 * Both elements use Dawn's AJAX cart (cart-drawer or cart-notification) so
 * Quick Add behaves identically to the primary Add to Cart button.
 * Both start hidden and reveal via [data-loaded] once content is injected.
 */

// ── Global deduplication registry ───────────────────────────────────────────
// Shared across all three carousels (Complete the Collection, Recently Viewed,
// Customers Also Bought). Seeded once from existing DOM cards so server-rendered
// Liquid cards block JS-injected duplicates.

const _shownIds = new Set();
let _seeded = false;

function _seedFromDOM() {
  if (_seeded) return;
  _seeded = true;
  // Seed from server-rendered carousel cards AND bundle widget items so JS
  // carousels (Recently Viewed, Customers Also Bought) skip those products.
  document.querySelectorAll(
    '.pce-rec-item[data-product-id], .pce-bundle__item[data-product-id]'
  ).forEach((el) => {
    _shownIds.add(String(el.dataset.productId));
  });
}

// Returns true and claims the id if not yet shown; false if already claimed.
function _claimId(id) {
  const key = String(id);
  if (_shownIds.has(key)) return false;
  _shownIds.add(key);
  return true;
}

// ── Shared utilities ─────────────────────────────────────────────────────────

function _formatMoney(amount, format) {
  if (!format) format = '${{amount}}';
  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  if (isNaN(num)) return String(amount);
  const decimal = num.toFixed(2);
  const noDecimal = String(Math.round(num));
  return format
    .replace(/\{\{\s*amount\s*\}\}/g, decimal)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimal)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, decimal.replace('.', ','))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimal);
}

function _getSizedImageUrl(src, size) {
  if (!src) return '';
  return src.replace(/(\.[^.?]+)(\?.*)?$/, `_${size}$1$2`);
}

function _esc(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ── AJAX Add to Cart ─────────────────────────────────────────────────────────
// Mirrors product-form.js: posts to routes.cart_add_url, appends section
// re-render keys, then calls cart.renderContents() and publishes cartUpdate.
// Falls back to cart page redirect when no cart element is present.

async function _ajaxAddToCart(variantId, button) {
  const cart =
    document.querySelector('cart-drawer') ||
    document.querySelector('cart-notification');

  button.setAttribute('aria-disabled', 'true');
  const originalHTML = button.innerHTML;
  button.innerHTML = '<span>Adding&hellip;</span>';

  try {
    // fetchConfig is defined in global.js (defer, loads before this script)
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((s) => s.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    const resp = await fetch(window.routes.cart_add_url, config);
    const data = await resp.json();

    if (data.status) {
      // Shopify returned an error (e.g. variant unavailable)
      console.error('[pce] cart error:', data.description || data.errors);
      return;
    }

    if (!cart) {
      window.location = window.routes.cart_url;
      return;
    }

    // publish is defined in pubsub.js; PUB_SUB_EVENTS in constants.js
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'product-recommendations',
      productVariantId: String(variantId),
      cartData: data,
    });

    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
    cart.renderContents(data);
  } catch (e) {
    console.error('[pce] ajax cart error:', e);
  } finally {
    button.removeAttribute('aria-disabled');
    button.innerHTML = originalHTML;
  }
}

// ── Recently Viewed Products ──────────────────────────────────────────────────

class RecentlyViewedProducts extends HTMLElement {
  static #STORAGE_KEY = 'amydavid_rv';
  static #MAX_STORE = 8;
  static #MAX_DISPLAY = 4;

  connectedCallback() {
    const handle = this.dataset.productHandle;
    if (handle) this.#trackHandle(handle);

    // Delegated click handler for Quick Add buttons (added before lazy load)
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-variant-id]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      _ajaxAddToCart(btn.dataset.variantId, btn);
    });

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.unobserve(this);
        this.#loadProducts();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    io.observe(this);
  }

  #trackHandle(handle) {
    try {
      const raw = localStorage.getItem(RecentlyViewedProducts.#STORAGE_KEY);
      const stored = raw ? JSON.parse(raw) : [];
      const updated = [handle, ...stored.filter((h) => h !== handle)].slice(
        0,
        RecentlyViewedProducts.#MAX_STORE
      );
      localStorage.setItem(
        RecentlyViewedProducts.#STORAGE_KEY,
        JSON.stringify(updated)
      );
    } catch {
      // Private/incognito or storage quota — silently skip
    }
  }

  async #loadProducts() {
    let handles;
    try {
      const raw = localStorage.getItem(RecentlyViewedProducts.#STORAGE_KEY);
      handles = raw ? JSON.parse(raw) : [];
    } catch {
      return;
    }

    const currentHandle = this.dataset.productHandle;
    handles = handles.filter((h) => h !== currentHandle);
    if (handles.length === 0) return;

    // Seed dedup registry from server-rendered cards before we start claiming
    _seedFromDOM();
    // Also block the current product (numeric ID from data attribute)
    const currentId = this.dataset.productId;
    if (currentId) _shownIds.add(String(currentId));

    // Fetch up to 2× needed so 404s or sold-out hidden items don't leave gaps
    const toFetch = handles.slice(0, RecentlyViewedProducts.#MAX_DISPLAY * 2);
    const results = await Promise.allSettled(
      toFetch.map((h) =>
        fetch(`/products/${encodeURIComponent(h)}.json`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => d?.product ?? null)
      )
    );

    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const cards = [];

    for (const result of results) {
      if (cards.length >= RecentlyViewedProducts.#MAX_DISPLAY) break;
      if (result.status !== 'fulfilled' || !result.value) continue;
      const p = result.value;
      if (!_claimId(p.id)) continue; // already shown elsewhere
      const card = this.#buildCard(p, fmt);
      if (card) cards.push(card);
    }

    if (cards.length === 0) return;

    this.innerHTML = `
      <h2 class="pce-recs__heading">Recently Viewed</h2>
      <div class="pce-recs__carousel" role="list">
        ${cards.join('')}
      </div>
    `;
    this.dataset.loaded = '';
  }

  #buildCard(product, moneyFormat) {
    const variant =
      product.variants?.find((v) => v.available) ?? product.variants?.[0];
    if (!variant) return '';

    const price = _formatMoney(variant.price, moneyFormat);
    const compareAtRaw = variant.compare_at_price;
    const compareAt =
      compareAtRaw && parseFloat(compareAtRaw) > parseFloat(variant.price)
        ? _formatMoney(compareAtRaw, moneyFormat)
        : null;

    const imgSrc = product.featured_image
      ? _getSizedImageUrl(product.featured_image, '600x600')
      : null;
    const imgAlt = _esc(product.images?.[0]?.alt || product.title);
    const imgHTML = imgSrc
      ? `<img src="${imgSrc}" class="pce-rec-item__img" loading="lazy" alt="${imgAlt}" width="600" height="600">`
      : '';

    const priceHTML = compareAt
      ? `<s class="pce-rec-item__price-compare">${compareAt}</s><span class="pce-rec-item__price-sale">${price}</span>`
      : price;

    const isAvailable = product.variants?.some((v) => v.available) ?? false;
    const isSingle = (product.variants?.length ?? 0) === 1;
    const titleEsc = _esc(product.title);
    const productUrl = `/products/${product.handle}`;

    let actionHTML;
    if (!isAvailable) {
      actionHTML = `<p class="pce-rec-item__sold-out">Sold Out</p>`;
    } else if (isSingle) {
      actionHTML = `
        <button
          type="button"
          class="pce-rec-item__quick-add"
          data-variant-id="${variant.id}"
          aria-label="Add to cart ${titleEsc}"
        >
          <span>Add to cart</span>
        </button>`;
    } else {
      actionHTML = `
        <a href="${productUrl}" class="pce-rec-item__quick-add pce-rec-item__quick-add--options">
          <span>View Options</span>
        </a>`;
    }

    const typeHTML = product.product_type
      ? `<p class="pce-rec-item__type">${_esc(product.product_type)}</p>`
      : '';

    return `
      <div class="pce-rec-item" role="listitem" data-product-id="${product.id}">
        <a href="${productUrl}" class="pce-rec-item__link" tabindex="0" aria-label="${titleEsc}">
          <div class="pce-rec-item__img-wrap">${imgHTML}</div>
          <div class="pce-rec-item__info">
            ${typeHTML}
            <p class="pce-rec-item__title">${titleEsc}</p>
            <p class="pce-rec-item__price">${priceHTML}</p>
          </div>
        </a>
        <div class="pce-rec-item__action-wrap">${actionHTML}</div>
      </div>`;
  }
}

// ── Customers Also Bought ─────────────────────────────────────────────────────

class CustomersAlsoBought extends HTMLElement {
  connectedCallback() {
    // Intercept form submits from Liquid-rendered single-variant cards
    this.addEventListener('submit', (e) => {
      e.preventDefault();
      const form = e.target.closest('.pce-rec-item__form');
      if (!form) return;
      const variantId = form.querySelector('[name="id"]')?.value;
      const btn = form.querySelector('button[type="submit"]');
      if (variantId && btn) _ajaxAddToCart(variantId, btn);
    });

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.unobserve(this);
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    io.observe(this);
  }

  async #load() {
    const { url, sectionId, productId } = this.dataset;
    if (!url || !productId) return;

    // Seed dedup registry and block the current product
    _seedFromDOM();
    _shownIds.add(String(productId));

    const fetchUrl = `${url}&product_id=${productId}&section_id=${
      sectionId || 'customers-also-bought'
    }`;

    let text;
    try {
      const resp = await fetch(fetchUrl);
      if (!resp.ok) return;
      text = await resp.text();
    } catch {
      return;
    }

    const html = new DOMParser().parseFromString(text, 'text/html');
    const allCards = Array.from(html.querySelectorAll('.pce-rec-item'));
    if (allCards.length === 0) return;

    // Deduplicate against everything already claimed (Complete the Collection,
    // Recently Viewed, and the current product), then limit to 4.
    const cards = [];
    for (const card of allCards) {
      if (cards.length >= 4) break;
      const id = card.dataset.productId;
      if (!id || !_claimId(id)) continue;
      cards.push(card);
    }

    if (cards.length === 0) return;

    const heading = document.createElement('h2');
    heading.className = 'pce-recs__heading';
    heading.textContent = 'Customers Also Bought';

    const carousel = document.createElement('div');
    carousel.className = 'pce-recs__carousel';
    carousel.setAttribute('role', 'list');
    cards.forEach((c) => carousel.appendChild(c));

    this.appendChild(heading);
    this.appendChild(carousel);
    this.dataset.loaded = '';
  }
}

// ── Bundle Widget ─────────────────────────────────────────────────────────────
// Powers the "Complete Your Collection" companion widget.
// Handles checkbox → total recalculation and multi-item AJAX cart add.

class BundleWidget extends HTMLElement {
  connectedCallback() {
    this._fmt = this.dataset.moneyFormat || '${{amount}}';
    this._form = this.querySelector('.pce-bundle__form');
    this._summary = this.querySelector('.pce-bundle__summary');
    this._err = this.querySelector('.pce-bundle__error');
    this._btn = this.querySelector('.pce-bundle__btn');
    if (!this._form) return;

    this._form.addEventListener('change', () => this._updateSummary());
    this._form.addEventListener('submit', (e) => {
      e.preventDefault();
      this._addToCart();
    });

    this._updateSummary();
  }

  _checked() {
    return Array.from(
      this._form.querySelectorAll('input[type="checkbox"]:checked')
    );
  }

  _updateSummary() {
    const checked = this._checked();
    if (checked.length === 0) {
      this._summary.hidden = true;
      this._btn.disabled = true;
      this._btn.textContent = 'Add to Cart';
      return;
    }
    const totalCents = checked.reduce(
      (sum, cb) => sum + parseInt(cb.dataset.priceCents || '0', 10),
      0
    );
    const label = checked.length === 1 ? '1 item selected' : `${checked.length} items selected`;
    this._summary.textContent = `${label} · ${_formatMoney(totalCents / 100, this._fmt)}`;
    this._summary.hidden = false;
    this._btn.disabled = false;
    this._btn.textContent = `Add ${checked.length} to Cart`;
  }

  async _addToCart() {
    const checked = this._checked();
    if (checked.length === 0) return;

    const cart =
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification');

    const origText = this._btn.textContent;
    this._btn.setAttribute('aria-disabled', 'true');
    this._btn.disabled = true;
    this._btn.textContent = 'Adding…';
    if (this._err) {
      this._err.hidden = true;
      this._err.textContent = '';
    }

    let addedSuccessfully = false;

    try {
      // Use JSON items[] body so all companions add in a single request.
      // fetchConfig sets Content-Type: application/json by default.
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      const payload = {
        items: checked.map((cb) => ({ id: Number(cb.value), quantity: 1 })),
      };
      if (cart) {
        payload.sections = cart.getSectionsToRender().map((s) => s.id);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }
      config.body = JSON.stringify(payload);

      const resp = await fetch(window.routes.cart_add_url, config);
      const data = await resp.json();

      if (data.status) {
        console.error('[pce] bundle cart error:', data.description || data.errors);
        if (this._err) {
          this._err.textContent = data.description || 'Could not add items — please try again.';
          this._err.hidden = false;
        }
        return;
      }

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      addedSuccessfully = true;
      this._btn.textContent = 'Added!';

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'bundle-widget',
        cartData: data,
      });

      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      cart.renderContents(data);
    } catch (e) {
      console.error('[pce] bundle error:', e);
      if (this._err) {
        this._err.textContent = 'Something went wrong — please try again.';
        this._err.hidden = false;
      }
    } finally {
      if (addedSuccessfully) {
        setTimeout(() => {
          this._btn.removeAttribute('aria-disabled');
          this._btn.disabled = false;
          this._updateSummary();
        }, 1500);
      } else {
        this._btn.removeAttribute('aria-disabled');
        this._btn.disabled = this._checked().length === 0;
        this._btn.textContent = origText;
      }
    }
  }
}

// ── Register custom elements ──────────────────────────────────────────────────

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
if (!customElements.get('customers-also-bought')) {
  customElements.define('customers-also-bought', CustomersAlsoBought);
}
if (!customElements.get('bundle-widget')) {
  customElements.define('bundle-widget', BundleWidget);
}
